import { deleteZone } from '../../../scripts/zone';
import { getDefaultId } from '../tableUtils';
import ProcessorChoiceField from '../fields/ProcessorChoiceField';
import ObjectAddressField from './fields/ObjectAddressField';
import NumberField from '../fields/NumberField';
import MuteAddressField, { hideMuteAddressField } from './fields/MuteAddressField';
import CheckboxField from '../fields/CheckBoxField';
import HierarchyTypeChoiceField from './fields/HierarchyTypeChoiceField';
import { addNewZone, saveUpdatedZones } from './zoneUtils';
import { DisplayField } from '../fields/Styles';
import { postEvent } from '../../../scripts/event';
import MasterZoneIdChoiceField, { hideMasterZoneIdField } from './fields/MasterZoneIdChoiceField';
import SourceSelectorChoiceField, { getSourceSelectorRequiredState } from './fields/SourceSelectorChoiceField';
import NameField from '../fields/NameField';
import { ZoneIcon } from '../styling/Icons';
import BackupZoneIdChoiceField from './fields/BackupZoneIdChoiceField';

const idField = 'zoneId';

export const getZoneTableProps = ({ customerId, zones, processors, sourceSelectors, hubs}) => {
    
    const getColumns = (zones) => [{
        key: 'zoneName',
        displayName: 'Name',
        form: true,
        width: '150px',
        render: (zone, updateZone, cellControl) => (
            <NameField
                object={zone}
                updateObject={updateZone}
                objects={zones}
                idField={'zoneId'}
                nameField={'zoneName'}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'zoneId',
        displayName: 'Id',
        width: '80px',
        readOnly: true,
        render: (zone) => (
            <DisplayField>{zone.zoneId}</DisplayField>
        )
    }, {
        key: 'processorId',
        displayName: 'Processor',
        width: '150px',
        form: true,
        getSuggested: () => getDefaultId(processors, 'processorId'),
        render: (zone, updateZone, cellControl) => (
            <ProcessorChoiceField
                object={zone}
                processors={processors}
                updateObject={updateZone}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'sourceSelectorId',
        displayName: 'Source selector',
        width: '150px',
        form: true,
        getSuggested: () => getDefaultId(sourceSelectors, 'sourceSelectorId'),
        getRequiredState: (zone) => getSourceSelectorRequiredState(zone, sourceSelectors),
        render: (zone, updateZone, cellControl) => (
            <SourceSelectorChoiceField
                zone={zone}
                updateZone={updateZone}
                sourceSelectors={sourceSelectors}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'objectAddress',
        displayName: 'Object address',
        width: '300px',
        form: true,
        render: (zone, updateZone, cellControl) => (
            <ObjectAddressField
                zone={zone}
                updateZone={updateZone}
                zones={zones}
                processors={processors}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'muteAddress',
        displayName: 'Mute address',
        width: '300px',
        hide: hideMuteAddressField(zones, processors),
        render: (zone, updateZone, cellControl) => (
            <MuteAddressField
                zone={zone}
                updateZone={updateZone}
                zones={zones}
                processors={processors}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'orderIndex',
        displayName: 'Order',
        width: '50px',
        render: (object, updateObject, cellControl) => (
            <NumberField
                value={object.orderIndex}
                setValue={(value) => updateObject({ orderIndex: value })}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'hide',
        displayName: 'Hide',
        width: '50px',
        render: (zone, updateZone, cellControl) => (
            <CheckboxField
                checked={zone.hide}
                setValue={() => updateZone({ hide: !zone.hide })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'shouldRegulate',
        displayName: 'Regulate',
        width: '50px',
        render: (zone, updateZone, cellControl) => (
            <CheckboxField
                checked={zone.shouldRegulate}
                setValue={() => updateZone({ shouldRegulate: !zone.shouldRegulate })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'autoResetZoneSettings',
        displayName: 'Autoreset',
        width: '50px',
        render: (zone, updateZone, cellControl) => (
            <CheckboxField
                checked={zone.autoResetZoneSettings}
                setValue={() => updateZone({ autoResetZoneSettings: !zone.autoResetZoneSettings })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'backupZoneId',
        displayName: 'Backup Zone',
        width: '150px',
        render: (zone, updateZone, cellControl) => (
            <BackupZoneIdChoiceField
                zone={zone}
                updateZone={updateZone}
                zones={zones}
                cellControl={cellControl}
            />
        )
    },{
        key: 'hierarchyType',
        displayName: 'Hierarchy',
        width: '100px',
        render: (zone, updateZone, cellControl) => (
            <HierarchyTypeChoiceField
                hierarchyType={zone.hierarchyType}
                setValue={(value) => updateZone({ hierarchyType: value })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'masterId',
        displayName: 'Master Zone',
        width: '150px',
        hide: hideMasterZoneIdField(zones),
        render: (zone, updateZone, cellControl) => (
            <MasterZoneIdChoiceField
                zone={zone}
                zones={zones}
                setValue={(value) => updateZone({ masterId: value })}
                cellControl={cellControl}
            />
        )
    }];

    const getListItemProps = (zone) => {
        return {
            displayName: zone.zoneName,
            Icon: ZoneIcon
        }
    } 

    const saveObjects = async (changedZones) => {
        try {
            await saveUpdatedZones(changedZones, zones, customerId);
        } catch (err) {
            console.log('save error', err);
        }
    }

    const deleteObject = async (zone) => {
        try {
            await deleteZone(customerId, zone.zoneId);
            await postEvent(zone.hubId, customerId, 'DELETE_ZONE', { zoneId: zone.zoneId });
        } catch (err) {
            console.log('Error failed to delete zone');
        }
    }

    const addObject = processors.length ? (newZone) => addNewZone(newZone, zones, processors, customerId) : null;

    const getActions = (zone) => [];

    const getPlaceholder = () => {
        return <div>
            {processors.length ? <>
                <p>No zones have been added.</p>
            </> : <>
                <p>Add a processor before adding zones</p>
            </>}
        </div>
    }

    return {
        title: 'Zones',
        getColumns: getColumns,
        parentObjects: zones,
        idField,
        saveObjects,
        deleteObject,
        addObject,
        getListItemProps,
        getActions,
        getPlaceholder
    }
}